<template>
  <div class="row">
    <div class="col-md-12">
      <h3>{{ $t('devices.title') }}</h3>
    </div>
  </div>

  <div class="row">
    <div class="col-md-12 mb-3">
      <card :title="$t('sidebar.gpsDevices')">
        <div class="d-flex flex-wrap align-items-center gap-2 mb-3 filter-row">
          <el-input
            v-model="searchQuery"
            class="search-input"
            clearable
            :prefix-icon="Search"
            :placeholder="$t('common.searchRecords')"
          />
          <div class="d-flex flex-wrap gap-2 ms-auto">
            <button
              class="filter-chip"
              :class="{ active: statusFilter === 'all' }"
              @click="setStatusFilter('all')"
            >
              {{ $t('devices.allDevices') }} · {{ devices.length }}
            </button>
            <button
              class="filter-chip chip-active"
              :class="{ active: statusFilter === 'inContact' }"
              @click="setStatusFilter('inContact')"
            >
              <span class="status-dot"></span>
              {{ $t('tools.deviceInContact') }} · {{ inContactCount }}
            </button>
            <button
              class="filter-chip chip-lost"
              :class="{ active: statusFilter === 'noContact' }"
              @click="setStatusFilter('noContact')"
            >
              <span class="status-dot"></span>
              {{ $t('tools.deviceNoContact') }} · {{ noContactCount }}
            </button>
            <button
              class="filter-chip chip-battery"
              :class="{ active: statusFilter === 'lowBattery' }"
              @click="setStatusFilter('lowBattery')"
            >
              <i class="fas fa-battery-quarter"></i>
              {{ $t('devices.lowBattery') }} · {{ lowBatteryCount }}
            </button>
          </div>
        </div>

        <div v-if="loading" class="text-muted">{{ $t('common.loading') || 'Loading...' }}</div>
        <div v-else-if="devices.length === 0" class="text-muted">{{ $t('devices.noDevices') }}</div>
        <template v-else>
          <div class="row devices-layout">
            <!-- Map: top on mobile, right on desktop -->
            <div class="col-12 col-lg-8 order-1 order-lg-2 mb-3 mb-lg-0">
              <div id="gpsDeviceOverviewMap" class="map map-big"></div>
            </div>
            <!-- List: below on mobile, left on desktop -->
            <div class="col-12 col-lg-4 order-2 order-lg-1">
              <div v-if="filteredDevices.length === 0" class="text-muted">
                {{ $t('devices.noMatches') }}
              </div>
              <ul v-else class="list-group device-list">
                <li
                  v-for="d in filteredDevices"
                  :key="rowKey(d)"
                  class="list-group-item device-row"
                  :class="{ selected: isActive(d) }"
                  @mouseenter="highlightDevice(d)"
                  @click="highlightDevice(d)"
                >
                  <div class="d-flex align-items-start">
                    <span class="asset-icon me-3" :class="markerColorClass(d)">
                      <i class="material-icons">{{ assetIcon(d) }}</i>
                    </span>
                    <div class="flex-grow-1 min-width-0">
                      <div class="d-flex align-items-center gap-2 min-width-0">
                        <router-link
                          :to="assetRoute(d)"
                          class="fw-semibold text-truncate asset-link"
                          @click.stop
                        >
                          {{ d.linkedAssetName }}
                        </router-link>
                        <span class="asset-type-label">{{ assetTypeLabel(d) }}</span>
                      </div>
                      <div class="text-sm text-muted text-truncate">
                        {{ d.deviceTypeName || $t('tools.deviceType') }} — {{ d.deviceId }}
                      </div>
                      <div class="d-flex flex-wrap align-items-center gap-2 mt-1">
                        <device-status-badge :last-contact="d.lastContact" />
                        <span v-if="d.batteryPct != null" class="text-sm battery-text">
                          <i class="fas" :class="batteryIcon(d.batteryPct)" :style="{ color: batteryColor(d.batteryPct) }"></i>
                          {{ Math.round(d.batteryPct) }}%
                        </span>
                      </div>
                      <div class="text-sm mt-1 location-line">
                        <template v-if="d.lastLocationTimestamp">
                          <span class="text-muted">{{ locationSummary(d) }}</span>
                          <span v-if="d.address" class="text-truncate d-block">{{ d.address }}</span>
                        </template>
                        <span v-else class="text-muted fst-italic">{{ $t('devices.noLocation') }}</span>
                      </div>
                    </div>
                    <div class="d-flex flex-column align-items-center gap-1 ms-2">
                      <a
                        v-if="d.lastLocationTimestamp"
                        role="button"
                        :title="$t('tools.showOnMap') || 'Show on map'"
                        @click.stop="focusOnDevice(d)"
                      >
                        <i class="material-icons">place</i>
                      </a>
                      <router-link
                        v-if="historyRoute(d)"
                        :to="historyRoute(d)"
                        :title="$t('devices.viewHistory')"
                        @click.stop
                      >
                        <i class="material-icons">history</i>
                      </router-link>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </template>
      </card>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import Card from '@/components/Card.vue';
import DeviceStatusBadge, { isInContact, appLocale } from '@/components/DeviceStatusBadge.vue';
import { Search } from '@element-plus/icons-vue';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Matches DeviceLinkedAssetType on the backend
const ASSET_TYPE = {
  Tool: 1,
  Car: 2,
  HeavyMachine: 3,
  ToolGroup: 4,
};

const LOW_BATTERY_PCT = 10;

export default {
  name: 'GpsDeviceOverview',
  components: { Card, DeviceStatusBadge },
  data() {
    return {
      Search,
      devices: [],
      loading: true,
      searchQuery: '',
      statusFilter: 'all',
      map: null,
      markers: {},
      activeKey: null,
    };
  },
  computed: {
    inContactCount() {
      return this.devices.filter(d => isInContact(d.lastContact)).length;
    },
    noContactCount() {
      return this.devices.length - this.inContactCount;
    },
    lowBatteryCount() {
      return this.devices.filter(d => d.batteryPct != null && d.batteryPct <= LOW_BATTERY_PCT).length;
    },
    filteredDevices() {
      let list = this.devices;
      if (this.statusFilter === 'inContact') {
        list = list.filter(d => isInContact(d.lastContact));
      } else if (this.statusFilter === 'noContact') {
        list = list.filter(d => !isInContact(d.lastContact));
      } else if (this.statusFilter === 'lowBattery') {
        list = list.filter(d => d.batteryPct != null && d.batteryPct <= LOW_BATTERY_PCT);
      }
      const q = this.searchQuery.trim().toLowerCase();
      if (q) {
        list = list.filter(d =>
          (d.linkedAssetName || '').toLowerCase().includes(q)
          || (d.deviceId || '').toLowerCase().includes(q)
          || (d.address || '').toLowerCase().includes(q));
      }
      return list;
    },
  },
  watch: {
    filteredDevices() {
      this.activeKey = null;
      this.renderMarkers();
    },
  },
  async mounted() {
    await this.fetchDevices();
    this.loading = false;
    this.$nextTick(() => this.initMap());
    window.addEventListener('resize', this.handleMapResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleMapResize);
    if (this._mapResizeTimer) clearTimeout(this._mapResizeTimer);
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },
  methods: {
    async fetchDevices() {
      try {
        const response = await api.get('/GeoTrace/DeviceOverview');
        this.devices = response.data;
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      }
    },
    setStatusFilter(filter) {
      this.statusFilter = this.statusFilter === filter ? 'all' : filter;
    },
    rowKey(d) {
      return `${d.deviceId}-${d.deviceTypeId}-${d.linkedAssetType}-${d.toolId || d.toolGroupId}`;
    },
    assetIcon(d) {
      switch (d.linkedAssetType) {
        case ASSET_TYPE.Car: return 'directions_car';
        case ASSET_TYPE.HeavyMachine: return 'agriculture';
        case ASSET_TYPE.ToolGroup: return 'category';
        default: return 'build';
      }
    },
    assetTypeLabel(d) {
      switch (d.linkedAssetType) {
        case ASSET_TYPE.Car: return this.$t('devices.typeCar');
        case ASSET_TYPE.HeavyMachine: return this.$t('devices.typeHeavyMachine');
        case ASSET_TYPE.ToolGroup: return this.$t('devices.typeToolGroup');
        default: return this.$t('devices.typeTool');
      }
    },
    assetRoute(d) {
      switch (d.linkedAssetType) {
        case ASSET_TYPE.Car: return { name: 'Car', params: { toolGroupId: d.toolGroupId } };
        case ASSET_TYPE.HeavyMachine: return { name: 'Heavy Machine', params: { toolGroupId: d.toolGroupId } };
        case ASSET_TYPE.ToolGroup: return { name: 'Tool Group', params: { toolGroupId: d.toolGroupId } };
        default: return { name: 'Tool', params: { toolId: d.toolId } };
      }
    },
    historyRoute(d) {
      switch (d.linkedAssetType) {
        case ASSET_TYPE.Car: return { name: 'Car GeoTraces', params: { toolGroupId: d.toolGroupId } };
        case ASSET_TYPE.HeavyMachine: return { name: 'Heavy Machine GeoTraces', params: { toolGroupId: d.toolGroupId } };
        case ASSET_TYPE.ToolGroup: return null;
        default: return { name: 'Tool GeoTraces', params: { toolId: d.toolId } };
      }
    },
    batteryColor(pct) {
      if (pct <= 10) return '#dc3545';
      if (pct <= 39) return '#ffc107';
      return '#28a745';
    },
    batteryIcon(pct) {
      if (pct <= 10) return 'fa-battery-empty';
      if (pct <= 39) return 'fa-battery-quarter';
      if (pct <= 64) return 'fa-battery-half';
      if (pct <= 89) return 'fa-battery-three-quarters';
      return 'fa-battery-full';
    },
    markerColorClass(d) {
      return isInContact(d.lastContact) ? 'asset-icon-active' : 'asset-icon-stale';
    },
    formatTimestamp(ts) {
      if (!ts) return '';
      try {
        return new Date(ts).toLocaleString(appLocale());
      } catch {
        return ts;
      }
    },
    locationSummary(d) {
      const date = this.formatTimestamp(d.lastLocationTimestamp);
      // Devices only report a new position when they move, so with a live
      // device an old last fix means "stationary", not "no signal"
      return isInContact(d.lastContact)
        ? this.$t('tools.stationarySince', { date })
        : this.$t('tools.lastKnownLocation', { date });
    },
    handleMapResize() {
      if (!this.map) return;
      if (this._mapResizeTimer) clearTimeout(this._mapResizeTimer);
      this._mapResizeTimer = setTimeout(() => {
        if (this.map) this.map.invalidateSize();
      }, 100);
    },
    initMap() {
      const el = document.getElementById('gpsDeviceOverviewMap');
      if (!el) return;
      this.map = L.map('gpsDeviceOverviewMap');
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(this.map);
      this.renderMarkers();
    },
    renderMarkers() {
      if (!this.map) return;
      Object.values(this.markers).forEach(m => m.remove());
      this.markers = {};

      const points = this.filteredDevices.filter(d => d.latitude != null && d.longitude != null);
      if (points.length === 0) {
        this.map.setView([65.6835, -18.1009], 5);
        return;
      }

      points.forEach(d => {
        const marker = L.marker([d.latitude, d.longitude], { icon: this.markerIconFor(d) })
          .addTo(this.map)
          .bindPopup(this.popupHtml(d));
        this.markers[this.rowKey(d)] = marker;
      });

      if (points.length === 1) {
        this.map.setView([points[0].latitude, points[0].longitude], 15);
      } else {
        const bounds = L.latLngBounds(points.map(p => [p.latitude, p.longitude]));
        this.map.fitBounds(bounds.pad(0.2));
      }
    },
    markerIconFor(d) {
      return L.divIcon({
        className: 'gps-device-marker',
        html: `<div class="marker-bubble ${this.markerColorClass(d)}"><i class="material-icons">${this.assetIcon(d)}</i></div>`,
        iconSize: [36, 36],
        iconAnchor: [18, 18],
      });
    },
    popupHtml(d) {
      const name = this.escape(d.linkedAssetName);
      const device = this.escape(`${d.deviceTypeName || ''} ${d.deviceId}`.trim());
      const battery = d.batteryPct != null
        ? `${this.escape(this.$t('tools.battery'))}: ${Math.round(d.batteryPct)}%<br>`
        : '';
      const location = this.escape(this.locationSummary(d));
      const address = d.address ? `${this.escape(d.address)}<br>` : '';
      return `<div><strong>${name}</strong><br>${device}<br>${battery}${address}<small>${location}</small></div>`;
    },
    escape(s) {
      if (s == null) return '';
      return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },
    isActive(d) {
      return this.activeKey === this.rowKey(d);
    },
    highlightDevice(d) {
      this.activeKey = this.rowKey(d);
      const m = this.markers[this.rowKey(d)];
      if (!m || !this.map) return;
      m.openPopup();
    },
    focusOnDevice(d) {
      const m = this.markers[this.rowKey(d)];
      if (!m || !this.map) return;
      this.activeKey = this.rowKey(d);
      this.map.setView(m.getLatLng(), 16);
      m.openPopup();
    },
  },
};
</script>

<style scoped>
.map-big {
  height: 420px;
  width: 100%;
}

@media (min-width: 992px) {
  .map-big {
    height: 600px;
  }
  .device-list {
    max-height: 600px;
    overflow-y: auto;
  }
}

.min-width-0 {
  min-width: 0;
}

.search-input {
  max-width: 280px;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 14px;
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid transparent;
  background-color: rgba(108, 117, 125, 0.12);
  color: #6c757d;
  cursor: pointer;
}

.filter-chip.active {
  border-color: currentColor;
}

.filter-chip .status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: currentColor;
  flex-shrink: 0;
}

.filter-chip.chip-active {
  color: #1e7e34;
  background-color: rgba(40, 167, 69, 0.12);
}

.filter-chip.chip-lost {
  color: #dc3545;
  background-color: rgba(220, 53, 69, 0.1);
}

.filter-chip.chip-battery {
  color: #997404;
  background-color: rgba(255, 193, 7, 0.18);
}

.asset-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  color: #fff;
  flex-shrink: 0;
}

.asset-icon i {
  font-size: 20px;
}

.asset-icon-active {
  background-color: #2dce89;
}

.asset-icon-stale {
  background-color: #8392ab;
}

.asset-type-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #8392ab;
  background-color: rgba(131, 146, 171, 0.12);
  border-radius: 8px;
  padding: 1px 8px;
  flex-shrink: 0;
}

.asset-link {
  color: #344767;
}

.asset-link:hover {
  text-decoration: underline;
}

.battery-text {
  font-weight: 600;
  color: #344767;
}

.device-row {
  cursor: pointer;
  transition: background-color 0.12s ease;
}

.device-row:hover,
.device-row.selected {
  background-color: rgba(94, 114, 228, 0.08);
}

/* Dark mode: light status colours on a tint of themselves (the light-mode ones are ~2.4:1 on navy) */
body.dark-version .filter-chip,
body.dark-version .asset-type-label {
  color: var(--ts-text-muted);
  background-color: rgba(168, 175, 192, 0.16);
}
body.dark-version .filter-chip.chip-active {
  color: var(--ts-success);
  background-color: rgba(95, 208, 138, 0.16);
}
body.dark-version .filter-chip.chip-lost {
  color: var(--ts-danger);
  background-color: rgba(255, 138, 128, 0.16);
}
body.dark-version .filter-chip.chip-battery {
  color: var(--ts-warning);
  background-color: rgba(246, 185, 74, 0.16);
}
body.dark-version .battery-text {
  color: var(--ts-text-strong);
}

@media (max-width: 767.98px) {
  .filter-row {
    flex-direction: column;
    align-items: stretch !important;
  }
  .search-input {
    max-width: none;
    width: 100%;
  }
  .filter-row .ms-auto {
    margin-left: 0 !important;
  }
}
</style>

<style>
.gps-device-marker .marker-bubble {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  border: 2px solid #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
}

.gps-device-marker .marker-bubble i {
  font-size: 20px;
}

.gps-device-marker .asset-icon-active {
  background-color: #2dce89;
}

.gps-device-marker .asset-icon-stale {
  background-color: #8392ab;
}
</style>
